import React, {ReactElement, useEffect, useState} from 'react';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import StateButton from 'app/general/widgets/StateButton';
import {useUpdateProcess} from 'app/process/api';
import {gt} from 'app/utils/i18n';

interface Props {
  show: boolean;
  isPrivate: boolean;
  url: string;
  processName: ReactElement;
  close: () => void;
}

type ShareMode =
  | 'processOnly'
  | 'processWithProcedures'
  | 'proceduresOnlyPublic'
  | 'proceduresOnlyPrivate';

export function ShareProcessModal({
  show,
  isPrivate,
  url,
  processName,
  close,
}: Props) {
  const futurePrivacyStatus = isPrivate ? 'public' : 'private';

  const [mode, setMode] = useState<ShareMode>('processOnly');

  const [updateProcess, {isLoading: isBusy, isSuccess}] = useUpdateProcess();

  useEffect(() => {
    if (isSuccess) {
      close();
    }
  }, [isSuccess]);

  const shareConfig: Record<
    ShareMode,
    {
      label: string;
      payload: Record<string, boolean>;
      message: string;
    }
  > = {
    processOnly: {
      label: gt('Make only %s process %s', processName, futurePrivacyStatus),
      payload: {
        private: !isPrivate,
      },
      message: gt('This process is now %s', futurePrivacyStatus),
    },

    processWithProcedures: {
      label: gt(
        'Make %s process and all procedures inside it %s as well',
        processName,
        futurePrivacyStatus
      ),
      payload: {
        private: !isPrivate,
        private_with_procedures: !isPrivate,
      },
      message: gt(
        'This process and all procedures inside it are now %s',
        futurePrivacyStatus
      ),
    },

    proceduresOnlyPublic: {
      label: gt(
        'Make all the procedures inside %s process public (process unchanged)',
        processName
      ),
      payload: {
        private_with_procedures: false,
      },
      message: gt('All procedures inside this process are now public'),
    },
    proceduresOnlyPrivate: {
      label: gt(
        'Make all the procedures inside %s process private (process unchanged)',
        processName
      ),
      payload: {
        private_with_procedures: true,
      },
      message: gt('All procedures inside this process are now private'),
    },
  };

  return (
    <TabbedModal
      show={show}
      onCancel={close}
      tabs={[
        {
          eventKey: 'process-procedure',
          name: gt('Share Publicly'),
          icon: 'fa fa-users',
          content: (
            <div>
              <div className="mx-5 mb-3">
                {Object.entries(shareConfig).map(([key, config]) => (
                  <div className="form-check" key={key}>
                    <input
                      className="form-check-input"
                      id={`share-mode-${key}`}
                      type="radio"
                      checked={mode === key}
                      onChange={() => setMode(key as ShareMode)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`share-mode-${key}`}
                    >
                      {config.label}
                    </label>
                  </div>
                ))}
              </div>
              <StyledModalFooter>
                <button className="btn btn-link text-secondary" onClick={close}>
                  <i className="fa fa-times mr-1" />
                  {gt('Close')}
                </button>
                <StateButton
                  className="btn btn-primary"
                  disabled={isBusy}
                  busyText={gt('Updating')}
                  isIdle={!isBusy}
                  onClick={() =>
                    updateProcess({
                      url,
                      ...shareConfig[mode].payload,
                      successMessage: shareConfig[mode].message,
                    })
                  }
                >
                  {gt('Confirm')}
                </StateButton>
              </StyledModalFooter>
            </div>
          ),
        },
      ]}
    />
  );
}
